import { useState, useMemo } from 'react';
import { useOsmNames } from '../../hooks/useOsmNames';
import OperationalOverview from './OperationalOverview';
import type { TargetItem } from './types';
import type { DashboardConfig } from '../../services/api';

function siteLabel(site: DashboardConfig): string {
  if (site.description?.trim()) return site.description.trim();
  return site.osm_name;
}

const DEFAULT_TARGETS: TargetItem[] = [
  { id: 'electricity', label: 'Electricity budget', current: 1024, target: 1400, unit: 'kWh',           color: '#1D9E75' },
  { id: 'gas',         label: 'Gas allowance',      current: 486,  target: 600,  unit: 'm³',            color: '#BA7517' },
  { id: 'cost',        label: 'Total cost cap',     current: 544,  target: 800,  unit: '£', prefix: '£', color: '#D4537E' },
  { id: 'carbon',      label: 'Carbon reduction',   current: 88,   target: 200,  unit: 'kg CO₂ saved',  color: '#378ADD' },
];

const selectStyle: React.CSSProperties = {
  fontFamily: "'DM Mono', monospace",
  fontSize: 12,
  padding: '5px 10px',
  borderRadius: 8,
  border: '0.5px solid var(--color-border-primary, rgba(0,0,0,0.2))',
  background: 'var(--color-bg-primary, #fff)',
  color: 'var(--color-text-primary, #2C2C2A)',
  cursor: 'pointer',
};

export default function OverviewDashboard() {
  const { data: siteList, loading: sitesLoading } = useOsmNames();
  const [selectedOsmName, setSelectedOsmName] = useState<string>('');

  const effectiveOsmName = selectedOsmName || siteList?.[0]?.osm_name || '';

  const config = useMemo(
    () => siteList?.find(s => s.osm_name === effectiveOsmName) ?? null,
    [siteList, effectiveOsmName],
  );

  if (sitesLoading) return null;

  if (!siteList?.length) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4rem' }}>
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: 'var(--color-text-secondary, #5F5E5A)' }}>
          No sites configured. Add a site in Dashboard Config to get started.
        </span>
      </div>
    );
  }

  const siteSelector = siteList.length > 1 ? (
    <select
      value={effectiveOsmName}
      onChange={e => setSelectedOsmName(e.target.value)}
      style={selectStyle}
      aria-label="Select site"
    >
      {siteList.map(site => (
        <option key={site.osm_name} value={site.osm_name}>
          {siteLabel(site)}
        </option>
      ))}
    </select>
  ) : undefined;

  return (
    <OperationalOverview
      osmName={effectiveOsmName}
      config={config}
      siteSelector={siteSelector}
    />
  );
}
